import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Smartphone } from 'lucide-react';
import { Slide01Hero } from './slides/Slide01Hero';
import { Slide02Moment } from './slides/Slide02Moment';
import { Slide03Approach } from './slides/Slide03Approach';
import { Slide04Snakker } from './slides/Slide04Snakker';
import { Slide05ERA } from './slides/Slide05ERA';
import { Slide06Flywheel } from './slides/Slide06Flywheel';
import { Slide07Value } from './slides/Slide07Value';
import { Slide08Future } from './slides/Slide08Future';
import { Teaser } from './Teaser';
const slides = [Slide01Hero, Slide02Moment, Slide03Approach, Slide04Snakker, Slide05ERA, Slide06Flywheel, Slide07Value, Slide08Future];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 50 : -50,
    opacity: 0
  })
};
export const Presentation = () => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [teaserOpen, setTeaserOpen] = useState(false);
  const paginate = useCallback((newDirection: number) => {
    setPage(([currentPage]) => {
      const nextPage = currentPage + newDirection;
      if (nextPage < 0 || nextPage >= slides.length) return [currentPage, 0];
      return [nextPage, newDirection];
    });
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (teaserOpen) return;
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        paginate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate, teaserOpen]);
  const CurrentSlide = slides[page];
  return <div className="w-full h-screen bg-black flex items-center justify-center overflow-hidden font-sans" data-id="element-2">
      {/* 16:9 Presentation Container */}
      <div className="relative w-full max-w-[1920px] aspect-video max-h-screen bg-slate-950 shadow-2xl overflow-hidden group" data-id="element-3">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-4">
          <motion.div key={page} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.4
          }
        }} className="absolute inset-0 w-full h-full" data-id="element-5">
            <CurrentSlide data-id="element-6" />
          </motion.div>
        </AnimatePresence>

        {/* Persistent Launch Teaser Button */}
        <button onClick={() => setTeaserOpen(true)} className="absolute top-6 right-6 z-50 group/btn flex items-center space-x-2 bg-red-600/90 hover:bg-red-500 backdrop-blur-md text-white text-xs font-semibold tracking-wide uppercase px-4 py-2.5 rounded-full shadow-[0_8px_30px_rgba(220,38,38,0.4)] border border-red-400/30 transition-all hover:scale-105 active:scale-95" aria-label="Launch mobile teaser preview" data-id="element-7">
          <Smartphone className="w-3.5 h-3.5" data-id="element-8" />
          <span data-id="element-9">Launch Teaser</span>
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-10" />
        </button>

        {/* Navigation Overlay */}
        <div className="absolute bottom-8 left-0 w-full px-12 flex items-center justify-between z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-11">
          {/* Slide Counter */}
          <div className="text-slate-400 font-mono text-sm tracking-widest" data-id="element-12">
            {String(page + 1).padStart(2, '0')} /{' '}
            {String(slides.length).padStart(2, '0')}
          </div>

          {/* Progress Dots */}
          <div className="flex items-center space-x-3" data-id="element-13">
            {slides.map((_, idx) => <button key={idx} onClick={() => setPage([idx, idx > page ? 1 : -1])} className={`transition-all duration-300 rounded-full ${idx === page ? 'w-8 h-1.5 bg-red-600' : 'w-1.5 h-1.5 bg-slate-600 hover:bg-slate-400'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-14" />)}
          </div>

          {/* Prev/Next Controls */}
          <div className="flex items-center space-x-2" data-id="element-15">
            <button onClick={() => paginate(-1)} disabled={page === 0} className="p-2 rounded-full bg-slate-900/50 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors backdrop-blur-sm border border-slate-700/50" data-id="element-16">
              <ChevronLeft className="w-5 h-5" data-id="element-17" />
            </button>
            <button onClick={() => paginate(1)} disabled={page === slides.length - 1} className="p-2 rounded-full bg-slate-900/50 text-slate-300 hover:bg-slate-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors backdrop-blur-sm border border-slate-700/50" data-id="element-18">
              <ChevronRight className="w-5 h-5" data-id="element-19" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Teaser Modal */}
      <Teaser open={teaserOpen} onClose={() => setTeaserOpen(false)} data-id="element-20" />
    </div>;
};